import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Wallet, AlertOctagon, Clock, ArrowDownToLine, ArrowUpFromLine,
  CreditCard, History, X, CheckCircle2,
  Printer, TrendingDown, AlertTriangle, Users, FileText,
  Truck, XCircle, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { InlineRowsSkeleton } from "../components/ui/Skeleton";
import { Button } from "../components/ui/Button";
import { Input, Label, Select, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, formatDate, formatDateTime, parseApiError} from "../lib/utils";
import { cn } from "../lib/utils";
import { useSort } from "../hooks/useSort";

// In bill công nợ chi tiết theo khách hàng + khoảng ngày
function printDebtBill({ customer, orders, payments, dateFrom, dateTo, shopName }) {
  const dateLabel = dateFrom || dateTo
    ? `${dateFrom || "..."} → ${dateTo || "..."}`
    : "Tất cả";

  const totalDebt    = orders.reduce((s, o) => {
    const rem = o.remaining_amount > 0 ? o.remaining_amount : Math.max(0, (o.total || 0) - (o.paid_amount || 0));
    return s + rem;
  }, 0);
  const totalPaid    = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const totalOrders  = orders.reduce((s, o) => s + (o.total || 0), 0);

  const orderRows = orders.map((o, i) => {
    const remaining = o.remaining_amount > 0 ? o.remaining_amount : Math.max(0, (o.total || 0) - (o.paid_amount || 0));
    // Chi tiết hàng hóa
    const itemsHtml = (o.items || []).map(it =>
      `<span class="item-tag">${it.name} ×${it.quantity}${it.unit ? " " + it.unit : ""}</span>`
    ).join(" ");
    const dueLabel = o.overdue
      ? `<span style="color:#dc2626;font-weight:bold">Quá hạn ${Math.abs(o.days_to_due || 0)}d</span>`
      : o.days_to_due != null ? `${o.days_to_due}d` : "—";
    return `
      <tr class="${i % 2 === 1 ? 'alt' : ''}">
        <td style="text-align:center">${i + 1}</td>
        <td><strong>${o.order_code || "—"}</strong></td>
        <td>${o.created_at ? o.created_at.slice(0, 10) : "—"}</td>
        <td class="items-cell">${itemsHtml || "—"}</td>
        <td class="num">${(o.total || 0).toLocaleString("vi-VN")}₫</td>
        <td class="num ok">${(o.paid_amount || 0).toLocaleString("vi-VN")}₫</td>
        <td class="num debt">${remaining > 0 ? remaining.toLocaleString("vi-VN") + "₫" : '<span style="color:#16a34a">✓ Hết</span>'}</td>
        <td style="text-align:center;font-size:10px">${dueLabel}</td>
      </tr>`;
  }).join("");

  const payRows = payments.length > 0 ? payments.map((p, i) => `
    <tr class="${i % 2 === 1 ? 'alt' : ''}">
      <td>${p.created_at ? p.created_at.slice(0, 10) : "—"}</td>
      <td>${p.order_code || "—"}</td>
      <td>${{ cash: "Tiền mặt", transfer: "Chuyển khoản", ewallet: "Ví điện tử", card: "Thẻ" }[p.method] || p.method}</td>
      <td class="num ok">+${(p.amount || 0).toLocaleString("vi-VN")}₫</td>
      <td style="font-size:10px;color:#555">${p.notes || "—"}</td>
      <td style="font-size:10px;color:#555">${p.created_by || "—"}</td>
    </tr>`).join("") : `<tr><td colspan="6" style="text-align:center;color:#888;padding:8px">Chưa có lần thu nào</td></tr>`;

  const html = `<!DOCTYPE html>
<html lang="vi">
<head>
<meta charset="UTF-8"/>
<title>Sao kê công nợ — ${customer.customer_name}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; padding: 20px; color: #222; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; border-bottom: 2px solid #2D4A22; padding-bottom: 12px; }
  .shop-name { font-size: 15px; font-weight: bold; color: #2D4A22; }
  .bill-title { font-size: 18px; font-weight: bold; text-align: center; margin: 12px 0 4px; }
  .bill-sub { text-align: center; color: #555; font-size: 11px; margin-bottom: 14px; }
  .info-box { background: #f9f9f9; border: 1px solid #ddd; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; font-size: 12px; }
  .info-box .lbl { color: #777; }
  .info-box .val { font-weight: 600; }
  .section-title { font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: .05em; color: #2D4A22; margin: 14px 0 6px; border-left: 3px solid #2D4A22; padding-left: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th { background: #2D4A22; color: white; font-size: 10px; text-transform: uppercase; padding: 5px 6px; text-align: left; }
  td { padding: 5px 6px; vertical-align: top; border-bottom: 1px solid #eee; font-size: 11px; }
  tr.alt td { background: #f5f5f5; }
  .num { text-align: right; font-family: monospace; white-space: nowrap; }
  .ok { color: #16a34a; }
  .debt { color: #dc2626; font-weight: bold; }
  .items-cell { max-width: 200px; }
  .item-tag { display: inline-block; background: #e8f5e9; border-radius: 3px; padding: 1px 4px; margin: 1px; font-size: 10px; white-space: nowrap; }
  .summary-box { background: #fff8e1; border: 1px solid #f59e0b; border-radius: 6px; padding: 10px 14px; margin-top: 14px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; text-align: center; }
  .sum-item .sum-val { font-size: 15px; font-weight: bold; font-family: monospace; }
  .sum-item .sum-lbl { font-size: 10px; color: #666; text-transform: uppercase; margin-top: 2px; }
  .footer { margin-top: 20px; display: flex; justify-content: space-between; font-size: 11px; color: #777; border-top: 1px solid #ddd; padding-top: 10px; }
  .sign-block { text-align: center; }
  .sign-block .sign-title { font-size: 11px; font-weight: bold; color: #333; margin-bottom: 40px; }
  .sign-block .sign-line { border-top: 1px solid #aaa; font-size: 10px; color: #888; padding-top: 4px; width: 120px; margin: 0 auto; }
  @media print { body { padding: 10mm; } }
</style>
</head>
<body>

<div class="header">
  <div>
    <div class="shop-name">${shopName || "Tiệm Bánh Bao"}</div>
    <div style="font-size:10px;color:#555">Ngày in: ${new Date().toLocaleDateString("vi-VN")}</div>
  </div>
  <div style="text-align:right;font-size:10px;color:#777">
    Khoảng thời gian: <strong>${dateLabel}</strong>
  </div>
</div>

<div class="bill-title">📋 SAO KÊ CÔNG NỢ KHÁCH HÀNG</div>
<div class="bill-sub">Tổng hợp chi tiết đơn hàng & lịch sử thanh toán</div>

<div class="info-box">
  <div><span class="lbl">Khách hàng:</span></div><div><span class="val">${customer.customer_name || "—"}</span></div>
  <div><span class="lbl">Số điện thoại:</span></div><div><span class="val">${customer.phone || "—"}</span></div>
  <div><span class="lbl">Số đơn:</span></div><div><span class="val">${orders.length} đơn</span></div>
  <div><span class="lbl">Kỳ sao kê:</span></div><div><span class="val">${dateLabel}</span></div>
</div>

<!-- Tóm tắt số liệu -->
<div class="summary-box">
  <div class="sum-item">
    <div class="sum-val" style="color:#1d4ed8">${totalOrders.toLocaleString("vi-VN")}₫</div>
    <div class="sum-lbl">Tổng giá trị đơn</div>
  </div>
  <div class="sum-item">
    <div class="sum-val" style="color:#16a34a">${totalPaid.toLocaleString("vi-VN")}₫</div>
    <div class="sum-lbl">Đã thu</div>
  </div>
  <div class="sum-item">
    <div class="sum-val" style="color:#dc2626">${totalDebt.toLocaleString("vi-VN")}₫</div>
    <div class="sum-lbl">Còn nợ</div>
  </div>
</div>

<!-- Chi tiết đơn hàng -->
<div class="section-title">Chi tiết đơn hàng (${orders.length} đơn)</div>
<table>
  <thead>
    <tr>
      <th style="width:28px;text-align:center">STT</th>
      <th>Mã đơn</th>
      <th>Ngày đặt</th>
      <th>Hàng hóa</th>
      <th style="text-align:right">Tổng đơn</th>
      <th style="text-align:right">Đã thu</th>
      <th style="text-align:right">Còn nợ</th>
      <th style="text-align:center">Hạn</th>
    </tr>
  </thead>
  <tbody>${orderRows || '<tr><td colspan="8" style="text-align:center;padding:10px;color:#888">Không có đơn nào trong kỳ này</td></tr>'}</tbody>
  <tfoot>
    <tr style="background:#f0f0f0">
      <td colspan="4" style="text-align:right;font-weight:bold;font-size:11px">TỔNG CỘNG</td>
      <td class="num" style="font-weight:bold">${totalOrders.toLocaleString("vi-VN")}₫</td>
      <td class="num ok" style="font-weight:bold">${totalPaid.toLocaleString("vi-VN")}₫</td>
      <td class="num debt">${totalDebt.toLocaleString("vi-VN")}₫</td>
      <td></td>
    </tr>
  </tfoot>
</table>

<!-- Lịch sử thanh toán -->
<div class="section-title">Lịch sử thu tiền (${payments.length} lần)</div>
<table>
  <thead>
    <tr>
      <th>Ngày thu</th>
      <th>Đơn hàng</th>
      <th>Phương thức</th>
      <th style="text-align:right">Số tiền thu</th>
      <th>Ghi chú</th>
      <th>Người thu</th>
    </tr>
  </thead>
  <tbody>${payRows}</tbody>
  ${payments.length > 0 ? `<tfoot><tr style="background:#f0f0f0"><td colspan="3" style="text-align:right;font-weight:bold">TỔNG ĐÃ THU</td><td class="num ok" style="font-weight:bold">+${totalPaid.toLocaleString("vi-VN")}₫</td><td colspan="2"></td></tr></tfoot>` : ""}
</table>

<div class="footer">
  <div style="font-size:11px;color:#666">
    In lúc: ${new Date().toLocaleString("vi-VN")} · Hệ thống quản lý ${shopName || "Tiệm Bánh Bao"}
  </div>
  <div style="display:flex;gap:40px">
    <div class="sign-block">
      <div class="sign-title">Khách hàng ký</div>
      <div class="sign-line">(Ký, ghi rõ họ tên)</div>
    </div>
    <div class="sign-block">
      <div class="sign-title">Người lập phiếu</div>
      <div class="sign-line">(Ký, ghi rõ họ tên)</div>
    </div>
  </div>
</div>

<script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
}

// ─── Sub-component: một hàng khách hàng có thể mở rộng ───────────────────────
function CustomerDebtRow({ d, onRefresh }) {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [activeTab, setActiveTab] = useState("orders"); // "orders" | "history"

  // Modal chi tiết khách hàng
  const [showDetail, setShowDetail] = useState(false);

  // Bộ lọc ngày trong modal
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Modal thu tiền
  const [collectModal, setCollectModal] = useState(null); // { mode: "order"|"all", order?: obj }
  const [payAmount, setPayAmount] = useState(0);
  const [payNotes, setPayNotes] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);

  // Modal chi tiết đơn hàng
  const [orderDetailModal, setOrderDetailModal] = useState(null); // order object
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  const openOrderDetail = async (e, orderId) => {
    e.stopPropagation();
    setOrderDetailLoading(true);
    setOrderDetailModal({ _loading: true });
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrderDetailModal(data);
    } catch {
      toast({ title: "Không thể tải chi tiết đơn", variant: "error" });
      setOrderDetailModal(null);
    } finally {
      setOrderDetailLoading(false);
    }
  };

  const loadDetail = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const [ordRes, payRes] = await Promise.all([
        api.get(`/debts/customers/${d.customer_id}/orders`),
        api.get("/debts/payments", { params: { customer_id: d.customer_id, direction: "in" } }),
      ]);
      setOrders(ordRes.data);
      setPayments(payRes.data);
    } finally {
      setLoadingOrders(false);
    }
  }, [d.customer_id]);

  const openDetail = () => {
    setActiveTab("orders");
    setDateFrom("");
    setDateTo("");
    loadDetail();
    setShowDetail(true);
  };

  // Lọc theo ngày
  const filteredOrders = orders.filter((o) => {
    const d2 = o.created_at ? o.created_at.slice(0, 10) : "";
    if (dateFrom && d2 < dateFrom) return false;
    if (dateTo && d2 > dateTo) return false;
    return true;
  });
  const filteredPayments = payments.filter((p) => {
    const d2 = p.created_at ? p.created_at.slice(0, 10) : "";
    if (dateFrom && d2 < dateFrom) return false;
    if (dateTo && d2 > dateTo) return false;
    return true;
  });

  // Mở modal thu tiền theo 1 đơn cụ thể
  const openCollectOrder = (o) => {
    const remaining = o.remaining_amount > 0
      ? o.remaining_amount
      : Math.max(0, (o.total || 0) - (o.paid_amount || 0));
    setCollectModal({ mode: "order", order: o });
    setPayAmount(remaining);
    setPayNotes("");
    setPayMethod("cash");
  };

  // Mở modal thu tiền toàn bộ tất cả đơn của khách
  const openCollectAll = async (e) => {
    e?.stopPropagation();
    let currentOrders = orders;
    if (currentOrders.length === 0) {
      setLoadingOrders(true);
      try {
        const [ordRes, payRes] = await Promise.all([
          api.get(`/debts/customers/${d.customer_id}/orders`),
          api.get("/debts/payments", { params: { customer_id: d.customer_id, direction: "in" } }),
        ]);
        currentOrders = ordRes.data;
        setOrders(ordRes.data);
        setPayments(payRes.data);
      } catch {
        toast({ title: "Không thể tải danh sách đơn nợ", variant: "error" });
        setLoadingOrders(false);
        return;
      }
      setLoadingOrders(false);
    }
    setCollectModal({ mode: "all", orders: currentOrders });
    setPayAmount(d.total_debt);
    setPayNotes("");
    setPayMethod("cash");
  };

  const submitCollect = async () => {
    if (!payAmount || Number(payAmount) <= 0) {
      toast({ title: "Vui lòng nhập số tiền hợp lệ", variant: "error" });
      return;
    }
    setSubmitting(true);
    try {
      if (collectModal.mode === "all") {
        // Phân bổ số tiền vào từng đơn theo thứ tự cũ → mới
        let remaining = Number(payAmount);
        const sorted = [...(collectModal.orders || orders)].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        for (const o of sorted) {
          if (remaining <= 0) break;
          const oDebt = o.remaining_amount > 0
            ? o.remaining_amount
            : Math.max(0, (o.total || 0) - (o.paid_amount || 0));
          if (oDebt <= 0) continue;
          const amt = Math.min(remaining, oDebt);
          await api.post("/debts/collect", {
            order_id: o.order_id,
            amount: amt,
            method: payMethod,
            notes: payNotes || `Thu tổng: phân bổ ${formatVND(amt)} cho đơn ${o.order_code}`,
          });
          remaining -= amt;
        }
        toast({ title: "Đã thu tiền thành công", variant: "success" });
      } else {
        // Thu 1 đơn cụ thể
        await api.post("/debts/collect", {
          order_id: collectModal.order.order_id,
          amount: Number(payAmount),
          method: payMethod,
          notes: payNotes,
        });
        toast({ title: "Đã thu tiền", variant: "success" });
      }
      setCollectModal(null);
      await loadDetail();
      onRefresh();
    } catch (e) {
      toast({ title: parseApiError(e, "Lỗi thu tiền"), variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = d.overdue
    ? <Badge variant="cancelled">Quá hạn</Badge>
    : d.due_soon
    ? <Badge variant="lowstock">Sắp đến hạn</Badge>
    : <Badge variant="regular">Trong hạn</Badge>;


  return (
    <>
      {/* HÀNG CHÍNH */}
      <tr
        className="border-t border-border transition-colors cursor-pointer hover:bg-cream/30"
        onClick={openDetail}
        data-testid={`debt-customer-${d.customer_id}`}
      >
        <td className="py-3 px-4">
          <div className="font-medium text-ink">{d.customer_name}</div>
          <div className="text-xs text-ink-muted">{d.phone}</div>
        </td>
        <td className="py-3 px-4 text-center text-sm">{d.order_count}</td>
        <td className="py-3 px-4 text-right font-mono font-semibold text-terracotta">{formatVND(d.total_debt)}</td>
        <td className="py-3 px-4 text-xs text-ink-muted">{d.earliest_due ? formatDate(d.earliest_due) : "—"}</td>
        <td className="py-3 px-4 text-center">{statusBadge}</td>
        <td className="py-3 px-4 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline" size="sm"
              onClick={(e) => { e.stopPropagation(); openCollectAll(e); }}
              className="text-xs"
              disabled={loadingOrders}
            >
              <CreditCard size={12} /> {loadingOrders ? "Đang tải..." : "Thu tiền"}
            </Button>
            <Button
              variant="ghost" size="icon"
              title="In sao kê công nợ"
              onClick={async (e) => {
                e.stopPropagation();
                // Load nếu chưa có
                let ords = orders, pays = payments;
                if (ords.length === 0) {
                  try {
                    const [ordRes, payRes] = await Promise.all([
                      api.get(`/debts/customers/${d.customer_id}/orders`),
                      api.get("/debts/payments", { params: { customer_id: d.customer_id, direction: "in" } }),
                    ]);
                    ords = ordRes.data; pays = payRes.data;
                    setOrders(ords); setPayments(pays);
                  } catch { toast({ title: "Lỗi tải dữ liệu", variant: "error" }); return; }
                }
                printDebtBill({ customer: d, orders: ords, payments: pays, dateFrom: "", dateTo: "", shopName: window.__SHOP_NAME__ || "" });
              }}
            >
              <Printer size={14} />
            </Button>
          </div>
        </td>
      </tr>

      {/* MODAL CHI TIẾT CÔNG NỢ KHÁCH HÀNG */}
      {showDetail && (
        <tr>
          <td colSpan={6} className="p-0">
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" onClick={() => setShowDetail(false)} />
              <div className="relative bg-white rounded-xl shadow-xl border border-border w-full max-w-3xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                  <div>
                    <h2 className="font-heading font-semibold text-lg">{d.customer_name}</h2>
                    <p className="text-xs text-ink-muted">{d.phone} · Tổng nợ: <span className="text-terracotta font-semibold font-mono">{formatVND(d.total_debt)}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openCollectAll(e); }}>
                      <CreditCard size={12} /> Thu tất cả
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      title="In sao kê công nợ theo kỳ"
                      onClick={(e) => {
                        e.stopPropagation();
                        printDebtBill({
                          customer: d,
                          orders: filteredOrders,
                          payments: filteredPayments,
                          dateFrom,
                          dateTo,
                          shopName: window.__SHOP_NAME__ || "",
                        });
                      }}
                    >
                      <Printer size={12} /> In sao kê
                    </Button>
                    <button
                      onClick={() => setShowDetail(false)}
                      className="text-ink-muted hover:text-ink rounded-lg p-1 hover:bg-surface-hover transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Bộ lọc ngày */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-border/50 bg-cream/30 shrink-0">
                  <span className="text-xs text-ink-muted font-medium">Lọc theo ngày:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="text-xs border border-border rounded-md px-2 py-1 bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-bamboo"
                    />
                    <span className="text-xs text-ink-muted">đến</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="text-xs border border-border rounded-md px-2 py-1 bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-bamboo"
                    />
                    {(dateFrom || dateTo) && (
                      <button
                        onClick={() => { setDateFrom(""); setDateTo(""); }}
                        className="text-xs text-bamboo hover:underline"
                      >Xóa lọc</button>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 px-5 pt-3 pb-0 border-b border-border/50 shrink-0">
                  <button
                    onClick={() => setActiveTab("orders")}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs font-semibold transition-colors border-b-2 -mb-px",
                      activeTab === "orders" ? "border-bamboo text-bamboo bg-cream/40" : "border-transparent text-ink-muted hover:text-ink"
                    )}
                  >
                    <AlertOctagon size={12} /> Đơn còn nợ ({filteredOrders.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("history")}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs font-semibold transition-colors border-b-2 -mb-px",
                      activeTab === "history" ? "border-bamboo text-bamboo bg-cream/40" : "border-transparent text-ink-muted hover:text-ink"
                    )}
                  >
                    <History size={12} /> Lịch sử thanh toán ({filteredPayments.length})
                  </button>
                </div>

                {/* Nội dung cuộn được */}
                <div className="overflow-y-auto flex-1 px-5 py-4">
                  {loadingOrders ? (
                    <InlineRowsSkeleton rows={3} />
                  ) : activeTab === "orders" ? (
                    filteredOrders.length === 0 ? (
                      <div className="flex items-center gap-2 py-8 text-xs text-emerald-700 justify-center">
                        <CheckCircle2 size={14} /> {orders.length === 0 ? "Khách đã thanh toán hết, không còn đơn nợ." : "Không có đơn nào trong khoảng ngày này."}
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-ink-muted sticky top-0 bg-white">
                          <tr>
                            <th className="text-left py-2 px-3">Mã đơn</th>
                            <th className="text-left py-2 px-3">Ngày tạo</th>
                            <th className="text-right py-2 px-3">Tổng đơn</th>
                            <th className="text-right py-2 px-3">Đã thu</th>
                            <th className="text-right py-2 px-3">Còn nợ</th>
                            <th className="text-center py-2 px-3">Hạn</th>
                            <th className="py-2 px-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.map((o) => {
                            const remaining = o.remaining_amount > 0
                              ? o.remaining_amount
                              : Math.max(0, (o.total || 0) - (o.paid_amount || 0));
                            return (
                              <tr key={o.order_id} className="border-t border-border/40 hover:bg-cream/30">
                                <td className="py-2 px-3">
                                  <button
                                    className="font-mono text-xs text-bamboo hover:underline flex items-center gap-1"
                                    onClick={(e) => openOrderDetail(e, o.order_id)}
                                    title="Xem chi tiết đơn"
                                  >
                                    {o.order_code}
                                    <ExternalLink size={10} className="opacity-60" />
                                  </button>
                                </td>
                                <td className="py-2 px-3 text-xs text-ink-muted">{formatDate(o.created_at)}</td>
                                <td className="py-2 px-3 text-right font-mono text-sm">{formatVND(o.total)}</td>
                                <td className="py-2 px-3 text-right font-mono text-sm text-emerald-700">{formatVND(o.paid_amount || 0)}</td>
                                <td className="py-2 px-3 text-right font-mono text-sm font-bold text-terracotta">{formatVND(remaining)}</td>
                                <td className="py-2 px-3 text-center text-xs">
                                  {o.overdue ? <Badge variant="cancelled">Quá {Math.abs(o.days_to_due)}d</Badge>
                                    : o.due_soon ? <Badge variant="lowstock">{o.days_to_due}d</Badge>
                                    : o.days_to_due != null ? <span className="text-ink-muted">{o.days_to_due}d</span>
                                    : "—"}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="text-xs px-2" onClick={(e) => openOrderDetail(e, o.order_id)} title="Chi tiết đơn">
                                      <FileText size={12} />
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-xs" onClick={() => openCollectOrder(o)}>
                                      Thu đơn này
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="border-t-2 border-border">
                          <tr>
                            <td colSpan={4} className="py-2 px-3 text-xs font-semibold text-right text-ink-muted">Tổng còn nợ:</td>
                            <td className="py-2 px-3 text-right font-bold text-terracotta font-mono">{formatVND(d.total_debt)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    )
                  ) : (
                    filteredPayments.length === 0 ? (
                      <div className="py-8 text-xs text-ink-muted text-center">Chưa có lịch sử thu tiền trong khoảng này.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-ink-muted sticky top-0 bg-white">
                          <tr>
                            <th className="text-left py-2 px-3">Thời gian</th>
                            <th className="text-left py-2 px-3">Đơn hàng</th>
                            <th className="text-left py-2 px-3">Phương thức</th>
                            <th className="text-right py-2 px-3">Số tiền thu</th>
                            <th className="text-left py-2 px-3">Ghi chú</th>
                            <th className="text-left py-2 px-3">Người thu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPayments.map((p) => (
                            <tr key={p.payment_id} className="border-t border-border/40">
                              <td className="py-2 px-3 text-xs text-ink-muted">{formatDateTime(p.created_at)}</td>
                              <td className="py-2 px-3 font-mono text-xs">{p.order_code || "—"}</td>
                              <td className="py-2 px-3 text-xs">{t(`payment.${p.method}`)}</td>
                              <td className="py-2 px-3 text-right font-mono font-semibold text-emerald-700">+{formatVND(p.amount)}</td>
                              <td className="py-2 px-3 text-xs text-ink-muted">{p.notes || "—"}</td>
                              <td className="py-2 px-3 text-xs text-ink-muted">{p.created_by || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-border">
                          <tr>
                            <td colSpan={3} className="py-2 px-3 text-xs font-semibold text-right text-ink-muted">Tổng đã thu:</td>
                            <td className="py-2 px-3 text-right font-bold text-emerald-700 font-mono">
                              +{formatVND(filteredPayments.reduce((s, p) => s + (p.amount || 0), 0))}
                            </td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    )
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* MODAL CHI TIẾT ĐƠN HÀNG */}
      {orderDetailModal && (
        <tr>
          <td colSpan={6} className="p-0">
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOrderDetailModal(null)} />
              <div className="relative bg-white rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                  <div>
                    <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                      <FileText size={16} className="text-bamboo" />
                      {orderDetailModal._loading ? "Đang tải..." : orderDetailModal.order_code}
                    </h2>
                    {!orderDetailModal._loading && (
                      <p className="text-xs text-ink-muted mt-0.5">
                        {formatDateTime(orderDetailModal.created_at)} · {orderDetailModal.customer_name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setOrderDetailModal(null)}
                    className="text-ink-muted hover:text-ink rounded-lg p-1.5 hover:bg-surface-hover transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                {orderDetailModal._loading ? (
                  <div className="flex items-center justify-center py-16 text-ink-muted text-sm gap-2">
                    <Clock size={16} className="animate-spin" /> Đang tải chi tiết đơn...
                  </div>
                ) : (
                  <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
                    {/* Trạng thái & thông tin nhanh */}
                    <div className="flex flex-wrap gap-3 items-center">
                      <Badge variant={orderDetailModal.status} className="text-sm px-3 py-1">
                        {orderDetailModal.status === "new" ? "Mới" :
                         orderDetailModal.status === "processing" ? "Đang xử lý" :
                         orderDetailModal.status === "delivering" ? "Đang giao" :
                         orderDetailModal.status === "delivered" ? "Đã giao" :
                         orderDetailModal.status === "debt_pending" ? "Chờ thu nợ" :
                         orderDetailModal.status === "cancelled" ? "Đã hủy" :
                         orderDetailModal.status}
                      </Badge>
                      <Badge variant={orderDetailModal.is_paid ? "delivered" : "preparing"}>
                        {orderDetailModal.is_paid ? "Đã thanh toán" : "Chưa thanh toán"}
                      </Badge>
                      <span className="text-xs text-ink-muted">
                        {orderDetailModal.payment_method === "cash" ? "Tiền mặt" :
                         orderDetailModal.payment_method === "transfer" ? "Chuyển khoản" :
                         orderDetailModal.payment_method === "debt" ? "Công nợ" :
                         orderDetailModal.payment_method === "ewallet" ? "Ví điện tử" :
                         orderDetailModal.payment_method}
                      </span>
                    </div>

                    {/* Sản phẩm */}
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Sản phẩm</div>
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-cream/50 text-xs uppercase text-ink-muted">
                            <tr>
                              <th className="text-left py-2 px-3">Tên</th>
                              <th className="text-right py-2 px-3">Đơn giá</th>
                              <th className="text-right py-2 px-3">SL</th>
                              <th className="text-right py-2 px-3">Thành tiền</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(orderDetailModal.items || []).map((it, i) => (
                              <tr key={i} className="border-t border-border/40">
                                <td className="py-2 px-3">{it.name}</td>
                                <td className="py-2 px-3 text-right font-mono text-xs">{formatVND(it.price)}</td>
                                <td className="py-2 px-3 text-right">{it.quantity}</td>
                                <td className="py-2 px-3 text-right font-medium">{formatVND(it.subtotal)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {/* Tổng */}
                        <div className="p-3 space-y-1 border-t border-border bg-cream/20 text-sm">
                          <div className="flex justify-between text-ink-muted">
                            <span>Tạm tính</span>
                            <span className="font-mono">{formatVND(orderDetailModal.subtotal)}</span>
                          </div>
                          {orderDetailModal.discount_percent > 0 && (
                            <div className="flex justify-between text-ink-muted">
                              <span>Giảm {orderDetailModal.discount_percent}%</span>
                              <span className="font-mono text-red-600">- {formatVND(Math.round((orderDetailModal.subtotal || 0) * orderDetailModal.discount_percent / 100))}</span>
                            </div>
                          )}
                          {orderDetailModal.discount > 0 && (
                            <div className="flex justify-between text-ink-muted">
                              <span>Giảm giá</span>
                              <span className="font-mono text-red-600">- {formatVND(orderDetailModal.discount)}</span>
                            </div>
                          )}
                          {orderDetailModal.shipping_fee > 0 && (
                            <div className="flex justify-between text-ink-muted">
                              <span>Phí giao hàng</span>
                              <span className="font-mono">+ {formatVND(orderDetailModal.shipping_fee)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-base border-t border-border pt-2 mt-1">
                            <span>Tổng cộng</span>
                            <span className="text-bamboo font-mono">{formatVND(orderDetailModal.total)}</span>
                          </div>
                          {(orderDetailModal.paid_amount > 0) && (
                            <div className="flex justify-between text-emerald-700">
                              <span>Đã thu</span>
                              <span className="font-mono">- {formatVND(orderDetailModal.paid_amount)}</span>
                            </div>
                          )}
                          {(orderDetailModal.remaining_amount > 0 || (orderDetailModal.total - orderDetailModal.paid_amount) > 0) && (
                            <div className="flex justify-between text-terracotta font-semibold">
                              <span>Còn nợ</span>
                              <span className="font-mono">
                                {formatVND(orderDetailModal.remaining_amount > 0
                                  ? orderDetailModal.remaining_amount
                                  : Math.max(0, (orderDetailModal.total || 0) - (orderDetailModal.paid_amount || 0)))}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Khách hàng & địa chỉ */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-cream/40 rounded-lg p-3 border border-border/50">
                        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Khách hàng</div>
                        <div className="font-medium">{orderDetailModal.customer_name}</div>
                        {orderDetailModal.customer_phone && <div className="text-xs text-ink-muted">{orderDetailModal.customer_phone}</div>}
                        {orderDetailModal.customer_address && <div className="text-xs text-ink-muted mt-0.5">{orderDetailModal.customer_address}</div>}
                      </div>
                      {orderDetailModal.note && (
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1.5">Ghi chú</div>
                          <div className="text-sm text-amber-900">{orderDetailModal.note}</div>
                        </div>
                      )}
                    </div>

                    {/* Timeline */}
                    {(orderDetailModal.timeline || []).length > 0 && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Lịch sử đơn</div>
                        <div className="space-y-2">
                          {orderDetailModal.timeline.map((entry, i) => (
                            <div key={i} className="flex gap-2.5 items-start">
                              <div className="w-6 h-6 rounded-full bg-bamboo text-white flex items-center justify-center shrink-0 mt-0.5">
                                {entry.status === "delivered" ? <CheckCircle2 size={11} /> :
                                 entry.status === "cancelled" ? <XCircle size={11} /> :
                                 entry.status === "delivering" ? <Truck size={11} /> :
                                 <Clock size={11} />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant={entry.status} className="text-xs">
                                    {entry.status === "new" ? "Mới" :
                                     entry.status === "processing" ? "Đang xử lý" :
                                     entry.status === "delivering" ? "Đang giao" :
                                     entry.status === "delivered" ? "Đã giao" :
                                     entry.status === "debt_pending" ? "Chờ thu nợ" :
                                     entry.status === "cancelled" ? "Đã hủy" :
                                     entry.status}
                                  </Badge>
                                  <span className="text-xs text-ink-muted">{formatDateTime(entry.at)}</span>
                                  {entry.by && <span className="text-xs text-ink-muted">· {entry.by}</span>}
                                </div>
                                {entry.note && <div className="text-xs text-ink mt-0.5">{entry.note}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Footer actions */}
                {!orderDetailModal._loading && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-border shrink-0 bg-cream/30">
                    <button
                      className="text-xs text-bamboo hover:underline flex items-center gap-1"
                      onClick={() => window.open(`/orders/${orderDetailModal.order_id}`, "_blank")}
                    >
                      <ExternalLink size={12} /> Mở trang đơn hàng đầy đủ
                    </button>
                    <div className="flex gap-2">
                      {(() => {
                        const debtOrder = filteredOrders.find(o => o.order_id === orderDetailModal.order_id);
                        if (debtOrder) return (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                            setOrderDetailModal(null);
                            openCollectOrder(debtOrder);
                          }}>
                            <CreditCard size={12} /> Thu đơn này
                          </Button>
                        );
                        return null;
                      })()}
                      <Button size="sm" variant="ghost" onClick={() => setOrderDetailModal(null)}>Đóng</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* MODAL THU TIỀN */}
      <Modal
        open={!!collectModal}
        onClose={() => setCollectModal(null)}
        title={
          collectModal?.mode === "all"
            ? `Thu tiền: ${d.customer_name}`
            : `Thu đơn: ${collectModal?.order?.order_code || ""}`
        }
        testId="collect-modal"
      >
        <div className="space-y-4">
          {/* Tóm tắt */}
          <div className="bg-cream rounded-lg p-3 text-sm space-y-1.5 border border-border">
            {collectModal?.mode === "all" ? (
              <>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Khách hàng:</span>
                  <span className="font-semibold">{d.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Số đơn còn nợ:</span>
                  <span>{orders.length} đơn</span>
                </div>
                <div className="flex justify-between border-t border-border pt-1.5">
                  <span className="font-semibold">Tổng còn nợ:</span>
                  <span className="font-bold text-terracotta">{formatVND(d.total_debt)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Tổng đơn:</span>
                  <span className="font-medium">{formatVND(collectModal?.order?.total || 0)}</span>
                </div>
                {(collectModal?.order?.paid_amount > 0) && (
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Đã thu trước:</span>
                    <span className="text-emerald-700 font-medium">− {formatVND(collectModal.order.paid_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-border pt-1.5">
                  <span className="font-semibold">Còn lại cần thu:</span>
                  <span className="font-bold text-terracotta">
                    {formatVND(
                      collectModal?.order?.remaining_amount > 0
                        ? collectModal.order.remaining_amount
                        : Math.max(0, (collectModal?.order?.total || 0) - (collectModal?.order?.paid_amount || 0))
                    )}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Số tiền thu */}
          <div>
            <Label>Số tiền thu</Label>
            <Input
              type="number"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              min={1}
              data-testid="collect-amount"
            />
            {collectModal?.mode === "all" && Number(payAmount) < d.total_debt && Number(payAmount) > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                ⚡ Thanh toán một phần — còn lại {formatVND(d.total_debt - Number(payAmount))} chưa thu.
              </p>
            )}
            {collectModal?.mode === "all" && Number(payAmount) >= d.total_debt && (
              <p className="text-xs text-emerald-600 mt-1">
                ✓ Thanh toán toàn bộ công nợ của khách này.
              </p>
            )}
          </div>

          {/* Phương thức */}
          <div>
            <Label>Phương thức thanh toán</Label>
            <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <option value="cash">Tiền mặt</option>
              <option value="transfer">Chuyển khoản</option>
              <option value="ewallet">Ví điện tử</option>
              <option value="card">Thẻ</option>
            </Select>
          </div>

          {/* Ghi chú */}
          <div>
            <Label>Ghi chú (tuỳ chọn)</Label>
            <Textarea value={payNotes} onChange={(e) => setPayNotes(e.target.value)} rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setCollectModal(null)}>Hủy</Button>
            <Button onClick={submitCollect} disabled={submitting} data-testid="collect-submit">
              {submitting ? "Đang xử lý..." : "Xác nhận thu tiền"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Debts() {
  const { t } = useI18n();
  const [tab, setTab] = useState("customer");
  const [customerData, setCustomerData] = useState(null);

  // ── Bộ lọc kỳ cho tab customer ─────────────────────────────────────────────
  const [periodPreset, setPeriodPreset] = useState("all"); // all | today | week | month | quarter | custom
  const [customFrom,   setCustomFrom]   = useState("");
  const [customTo,     setCustomTo]     = useState("");

  const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  // Tính dateFrom / dateTo từ preset
  const periodRange = useMemo(() => {
    const n = new Date();
    if (periodPreset === "today") {
      const s = fmtD(n);
      return { from: s, to: s, label: "Hôm nay" };
    }
    if (periodPreset === "yesterday") {
      const y = new Date(n); y.setDate(n.getDate()-1);
      const s = fmtD(y);
      return { from: s, to: s, label: "Hôm qua" };
    }
    if (periodPreset === "week") {
      const dow = n.getDay() || 7;
      const mon = new Date(n); mon.setDate(n.getDate()-dow+1);
      return { from: fmtD(mon), to: fmtD(n), label: "Tuần này" };
    }
    if (periodPreset === "month") {
      return { from: fmtD(new Date(n.getFullYear(), n.getMonth(), 1)), to: fmtD(n), label: `Tháng ${n.getMonth()+1}` };
    }
    if (periodPreset === "quarter") {
      const q = Math.floor(n.getMonth()/3);
      return { from: fmtD(new Date(n.getFullYear(), q*3, 1)), to: fmtD(n), label: `Quý ${q+1}` };
    }
    if (periodPreset === "custom") {
      return { from: customFrom, to: customTo, label: `${customFrom||"..."} → ${customTo||"..."}` };
    }
    return { from: "", to: "", label: "Tất cả thời gian" };
  }, [periodPreset, customFrom, customTo]);

  // Lọc danh sách khách theo kỳ (lọc theo ngày tạo đơn gần nhất hoặc last_order)
  const filteredCustomers = useMemo(() => {
    if (!customerData) return [];
    const { from, to } = periodRange;
    if (!from && !to) return customerData.items;
    return customerData.items.filter((d) => {
      const lo = d.last_order ? d.last_order.slice(0,10) : "";
      if (from && lo && lo < from) return false;
      if (to   && lo && lo > to)   return false;
      return true;
    });
  }, [customerData, periodRange]);

  const filteredTotal    = filteredCustomers.reduce((s, d) => s + (d.total_debt || 0), 0);
  const filteredOverdue  = filteredCustomers.filter(d => d.overdue).length;
  const filteredDueSoon  = filteredCustomers.filter(d => d.due_soon && !d.overdue).length;

  const handlePrint = () => {
    let html = '';
    const style = `<style>
      *{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:12px;margin:0;padding:20px;color:#222}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #2D4A22;padding-bottom:10px;margin-bottom:14px}
      .shop{font-size:15px;font-weight:bold;color:#2D4A22}.period-lbl{font-size:11px;color:#555;margin-top:2px}
      h2{margin:0 0 2px;font-size:16px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
      .kpi{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:14px}
      .kpi-box{border:1px solid #ddd;border-radius:6px;padding:8px 12px;text-align:center}
      .kpi-box .val{font-size:15px;font-weight:bold;font-family:monospace}.kpi-box .lbl{font-size:10px;color:#666;text-transform:uppercase;margin-top:2px}
      table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left;font-size:11px}
      th{background:#e8f5e9;font-size:10px;text-transform:uppercase;font-weight:bold}
      tfoot td{font-weight:bold;background:#d4edda}tr:nth-child(even) td{background:#fafafa}
      .overdue{color:#dc2626;font-weight:bold}.duesoon{color:#d97706;font-weight:bold}.ok{color:#16a34a}
      .footer{margin-top:16px;font-size:10px;color:#777;border-top:1px solid #ddd;padding-top:8px;display:flex;justify-content:space-between}
      @media print{body{padding:10mm}}
    </style>`;

    if (tab === 'customer' && customerData) {
      const items = filteredCustomers;
      const total = items.reduce((s,d) => s+(d.total_debt||0), 0);
      const overdueCnt = items.filter(d=>d.overdue).length;
      const dueSoonCnt = items.filter(d=>d.due_soon&&!d.overdue).length;
      const rows = items.map((d, i) => {
        const statusCls = d.overdue ? "overdue" : d.due_soon ? "duesoon" : "ok";
        const statusLbl = d.overdue ? "⚠ Quá hạn" : d.due_soon ? "⏰ Sắp hạn" : "✓ Trong hạn";
        return `<tr>
          <td style="text-align:center">${i+1}</td>
          <td><strong>${d.customer_name||''}</strong><br/><small style="color:#888">${d.phone||''}</small></td>
          <td style="text-align:right;color:#dc2626;font-family:monospace;font-weight:bold">${(d.total_debt||0).toLocaleString('vi-VN')}₫</td>
          <td style="text-align:center">${d.order_count||0}</td>
          <td style="text-align:center;font-size:10px">${d.earliest_due?d.earliest_due.slice(0,10):'—'}</td>
          <td style="text-align:center;font-size:10px">${d.last_order?d.last_order.slice(0,10):'—'}</td>
          <td class="${statusCls}" style="text-align:center;font-size:10px">${statusLbl}</td>
        </tr>`;
      }).join('');
      html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/><title>Công nợ KH — ${periodRange.label}</title>${style}</head><body>
      <div class="header">
        <div><div class="shop">${window.__SHOP_NAME__||'Tiệm Bánh Bao'}</div><div class="period-lbl">Ngày in: ${new Date().toLocaleDateString('vi-VN')}</div></div>
        <div style="text-align:right;font-size:11px;color:#555">Kỳ: <strong>${periodRange.label}</strong></div>
      </div>
      <h2>📋 Danh sách công nợ khách hàng</h2>
      <p class="sub">${items.length} khách · ${overdueCnt} quá hạn · ${dueSoonCnt} sắp hạn</p>
      <div class="kpi">
        <div class="kpi-box"><div class="val" style="color:#dc2626">${total.toLocaleString('vi-VN')}₫</div><div class="lbl">Tổng nợ</div></div>
        <div class="kpi-box"><div class="val" style="color:#d97706">${overdueCnt}</div><div class="lbl">Quá hạn</div></div>
        <div class="kpi-box"><div class="val" style="color:#f59e0b">${dueSoonCnt}</div><div class="lbl">Sắp đến hạn</div></div>
      </div>
      <table>
        <thead><tr><th style="width:28px;text-align:center">STT</th><th>Khách hàng</th><th style="text-align:right">Tổng nợ</th><th style="text-align:center">Số đơn</th><th style="text-align:center">Hạn gần nhất</th><th style="text-align:center">Đơn cuối</th><th style="text-align:center">Trạng thái</th></tr></thead>
        <tbody>${rows||'<tr><td colspan="7" style="text-align:center;padding:12px;color:#888">Không có dữ liệu trong kỳ này</td></tr>'}</tbody>
        <tfoot><tr><td colspan="2" style="text-align:right">TỔNG NỢ (${items.length} khách)</td><td style="text-align:right;color:#dc2626">${total.toLocaleString('vi-VN')}₫</td><td colspan="4"></td></tr></tfoot>
      </table>
      <div class="footer"><span>In lúc: ${new Date().toLocaleString('vi-VN')}</span><span>Kỳ: ${periodRange.label}</span></div>
      <script>window.onload=()=>{window.print()}<\/script></body></html>`;
    } else if (tab === 'supplier' && suppliers.length > 0) {
      const rows = suppliers.map((s, i) =>
        `<tr><td>${i+1}</td><td>${s.code||'—'}</td><td>${s.name||''}</td><td>${s.phone||'—'}</td><td style="text-align:right;color:#dc2626">${(s.total_debt||0).toLocaleString('vi-VN')}₫</td></tr>`
      ).join('');
      const total = suppliers.reduce((sum, s) => sum + (s.total_debt||0), 0);
      html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/><title>Công nợ NCC</title>${style}</head><body>
      <h2>Công nợ nhà cung cấp</h2><p class="sub">${suppliers.length} NCC · Tổng nợ: ${total.toLocaleString('vi-VN')}₫</p>
      <table><thead><tr><th>STT</th><th>Mã NCC</th><th>Tên NCC</th><th>SĐT</th><th style="text-align:right">Công nợ</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="4" style="text-align:right">TỔNG NỢ</td><td style="text-align:right;color:#dc2626">${total.toLocaleString('vi-VN')}₫</td></tr></tfoot>
      </table><script>window.onload=()=>{window.print()}<\/script></body></html>`;
    } else if (tab === 'history' && payments.length > 0) {
      const rows = payments.map((p, i) =>
        `<tr><td>${i+1}</td><td>${p.created_at?p.created_at.slice(0,10):''}</td><td>${p.direction==='in'?'Thu':'Chi'}</td><td>${p.customer_name||p.supplier_name||'—'}</td><td>${p.method||''}</td><td style="text-align:right;color:${p.direction==='in'?'#059669':'#dc2626'}">${p.direction==='in'?'+':'−'}${(p.amount||0).toLocaleString('vi-VN')}₫</td><td>${p.notes||''}</td></tr>`
      ).join('');
      html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/><title>Lịch sử thanh toán</title>${style}</head><body>
      <h2>Lịch sử thanh toán</h2><p class="sub">${payments.length} giao dịch</p>
      <table><thead><tr><th>STT</th><th>Ngày</th><th>Hướng</th><th>Đối tượng</th><th>Phương thức</th><th style="text-align:right">Số tiền</th><th>Ghi chú</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.onload=()=>{window.print()}<\/script></body></html>`;
    } else { return; }
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // ── Sort hooks ──────────────────────────────────────────────────────────────
  const { sorted: sortedCustomers, handleSort: handleSortCustomer, SortIcon: SortIconCustomer } =
    useSort(filteredCustomers, "total_debt", "desc");
  const { sorted: sortedSuppliers, handleSort: handleSortSupplier, SortIcon: SortIconSupplier } =
    useSort(suppliers, "total_debt", "desc");
  const { sorted: sortedPayments, handleSort: handleSortPayments, SortIcon: SortIconPayments } =
    useSort(payments, "created_at", "desc");

  // Supplier pay modal
  const [payModal, setPayModal] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [payNotes, setPayNotes] = useState("");
  const [payMethod, setPayMethod] = useState("cash");

  const load = useCallback(async () => {
    if (tab === "customer") {
      const { data } = await api.get("/debts/customers");
      setCustomerData(data);
    }
    if (tab === "supplier") {
      const [s, p] = await Promise.all([
        api.get("/suppliers"),
        api.get("/debts/payments", { params: { direction: "out" } }),
      ]);
      setSuppliers(s.data);
      setPayments(p.data);
    }
    if (tab === "history") {
      const { data } = await api.get("/debts/payments");
      setPayments(data);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openPaySupplier = (s) => {
    setPayModal(s);
    setPayAmount(0);
    setPayNotes("");
    setPayMethod("cash");
  };

  const submitPaySupplier = async () => {
    try {
      await api.post("/debts/pay-supplier", {
        supplier_id: payModal.supplier_id,
        amount: Number(payAmount),
        method: payMethod,
        notes: payNotes,
      });
      toast({ title: "Đã ghi nhận thanh toán", variant: "success" });
      setPayModal(null);
      load();
    } catch (e) {
      toast({ title: parseApiError(e) || t("common.error"), variant: "error" });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="debts-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wallet size={28} className="text-bamboo" /> {t("debts.title")}
        </h1>
        <p className="text-sm text-ink-secondary mt-1">{t("debts.customerDebts")} & {t("debts.supplierDebts")}</p>
      </div>

      {/* ── Bộ lọc kỳ (chỉ hiện cho tab công nợ KH) ── */}
      {tab === "customer" && (
        <div className="flex items-center gap-2 flex-wrap">
          {/* Preset nhanh */}
          {[
            { id: "all",       label: "Tất cả" },
            { id: "today",     label: "Hôm nay" },
            { id: "yesterday", label: "Hôm qua" },
            { id: "week",      label: "Tuần này" },
            { id: "month",     label: "Tháng này" },
            { id: "quarter",   label: "Quý này" },
            { id: "custom",    label: "Tuỳ chọn..." },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => setPeriodPreset(p.id)}
              className={cn(
                "px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all",
                periodPreset === p.id
                  ? "border-bamboo bg-bamboo text-white"
                  : "border-border text-ink-muted hover:border-bamboo/50 hover:text-bamboo"
              )}
            >
              {p.label}
            </button>
          ))}

          {/* Custom date range */}
          {periodPreset === "custom" && (
            <div className="flex items-center gap-2 ml-1 pl-3 border-l border-border">
              <input
                type="date" value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="h-8 text-xs px-2 border border-border rounded-lg bg-white"
              />
              <span className="text-ink-muted text-xs">→</span>
              <input
                type="date" value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="h-8 text-xs px-2 border border-border rounded-lg bg-white"
              />
            </div>
          )}

          {/* Kết quả lọc */}
          {periodPreset !== "all" && customerData && (
            <span className="text-xs text-ink-muted bg-cream px-3 py-1.5 rounded-xl border border-border ml-1">
              <span className="font-semibold text-bamboo">{filteredCustomers.length}</span> khách ·{" "}
              <span className="font-semibold text-red-600">{formatVND(filteredTotal)}</span> nợ
            </span>
          )}

          <div className="flex-1" />
          <Button variant="outline" onClick={handlePrint}>
            <Printer size={14} />
            In{tab === "customer" && periodPreset !== "all" ? ` (${periodRange.label})` : " danh sách"}
          </Button>
        </div>
      )}
      {tab !== "customer" && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={handlePrint}><Printer size={14} /> In danh sách</Button>
        </div>
      )}

      {/* TABS */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: "customer", label: t("debts.customerDebts"), icon: ArrowDownToLine },
          { id: "supplier", label: t("debts.supplierDebts"), icon: ArrowUpFromLine },
          { id: "history",  label: t("debts.paymentHistory"), icon: Clock },
        ].map((x) => {
          const Icon = x.icon;
          return (
            <button
              key={x.id}
              onClick={() => setTab(x.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px",
                tab === x.id ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"
              )}
              data-testid={`debts-tab-${x.id}`}
            >
              <Icon size={14} /> {x.label}
            </button>
          );
        })}
      </div>

      {/* CÔNG NỢ KHÁCH HÀNG */}
      {tab === "customer" && customerData && (
        <>
          {/* KPI cards - vivid redesign */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Tổng công nợ */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-bamboo to-bamboo-light text-white shadow-md group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 70% 20%, white 0%, transparent 60%)'}} />
              <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-white/70">{t("debts.totalDebt")}</span>
                  <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Wallet size={14} className="text-white" />
                  </div>
                </div>
                <div className="font-heading text-xl font-bold tracking-tight leading-none">{formatVND(customerData.total)}</div>
                <div className="flex items-center gap-1 mt-2 text-white/70 text-[11px]">
                  <Users size={10} />
                  <span>{customerData.items.length} khách đang nợ</span>
                </div>
                <div className="mt-2 h-0.5 rounded-full bg-white/20">
                  <div className="h-0.5 rounded-full bg-white/60 w-full" />
                </div>
              </div>
            </div>

            {/* Sắp đến hạn */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-amber-500 to-orange-400 text-white shadow-md group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 70% 20%, white 0%, transparent 60%)'}} />
              <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-white/70">{t("debts.dueSoon")}</span>
                  <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Clock size={14} className="text-white" />
                  </div>
                </div>
                <div className="font-heading text-xl font-bold tracking-tight leading-none">{formatVND(customerData.due_soon_total)}</div>
                <div className="flex items-center gap-1 mt-2 text-white/70 text-[11px]">
                  <AlertTriangle size={10} />
                  <span>Sắp đến hạn thanh toán</span>
                </div>
                <div className="mt-2 h-0.5 rounded-full bg-white/20">
                  <div className="h-0.5 rounded-full bg-white/60"
                    style={{width: customerData.total > 0 ? `${Math.min(100, (customerData.due_soon_total / customerData.total) * 100)}%` : '0%'}} />
                </div>
              </div>
            </div>

            {/* Quá hạn */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-red-600 to-rose-500 text-white shadow-md group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 70% 20%, white 0%, transparent 60%)'}} />
              <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
              {customerData.overdue_total > 0 && (
                <div className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-white animate-ping opacity-75" />
              )}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-white/70">{t("debts.overdue")}</span>
                  <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <AlertOctagon size={14} className="text-white" />
                  </div>
                </div>
                <div className="font-heading text-xl font-bold tracking-tight leading-none">{formatVND(customerData.overdue_total)}</div>
                <div className="flex items-center gap-1 mt-2 text-white/70 text-[11px]">
                  <TrendingDown size={10} />
                  <span>{customerData.overdue_total > 0 ? "Cần thu gấp!" : "Không có công nợ quá hạn"}</span>
                </div>
                <div className="mt-2 h-0.5 rounded-full bg-white/20">
                  <div className="h-0.5 rounded-full bg-white/60"
                    style={{width: customerData.total > 0 ? `${Math.min(100, (customerData.overdue_total / customerData.total) * 100)}%` : '0%'}} />
                </div>
              </div>
            </div>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortCustomer("customer_name")}>Khách hàng {SortIconCustomer("customer_name")}</th>
                    <th className="text-center py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortCustomer("order_count")}>Số đơn {SortIconCustomer("order_count")}</th>
                    <th className="text-right py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortCustomer("total_debt")}>Tổng nợ {SortIconCustomer("total_debt")}</th>
                    <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortCustomer("earliest_due")}>Hạn gần nhất {SortIconCustomer("earliest_due")}</th>
                    <th className="text-center py-3 px-4">Trạng thái</th>
                    <th className="py-3 px-4 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody data-testid="debts-customer-table">
                  {sortedCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-ink-muted">
                        {periodPreset !== "all" ? `Không có công nợ trong kỳ: ${periodRange.label}` : t("common.empty")}
                      </td>
                    </tr>
                  ) : sortedCustomers.map((d) => (
                    <CustomerDebtRow key={d.customer_id} d={d} onRefresh={load} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* CÔNG NỢ NHÀ CUNG CẤP */}
      {tab === "supplier" && (
        <Card>
          <CardHeader><CardTitle>{t("debts.supplierDebts")}</CardTitle></CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                <tr>
                  <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortSupplier("code")}>Mã NCC {SortIconSupplier("code")}</th>
                  <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortSupplier("name")}>Tên NCC {SortIconSupplier("name")}</th>
                  <th className="text-left py-3 px-4">SĐT</th>
                  <th className="text-right py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortSupplier("total_debt")}>Công nợ {SortIconSupplier("total_debt")}</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody data-testid="debts-supplier-table">
                {sortedSuppliers.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
                ) : sortedSuppliers.map((s) => (
                  <tr key={s.supplier_id} className="border-t border-border" data-testid={`debt-supplier-${s.supplier_id}`}>
                    <td className="py-3 px-4 font-mono text-xs">{s.code}</td>
                    <td className="py-3 px-4 font-medium">{s.name}</td>
                    <td className="py-3 px-4 text-xs">{s.phone}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatVND(s.total_debt || 0)}</td>
                    <td className="py-3 px-4 text-right">
                      <Button size="sm" onClick={() => openPaySupplier(s)} data-testid={`debt-pay-${s.supplier_id}`}>
                        {t("debts.payToSupplier")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* LỊCH SỬ THANH TOÁN TOÀN BỘ */}
      {tab === "history" && (
        <Card>
          <CardHeader><CardTitle>{t("debts.paymentHistory")}</CardTitle></CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                <tr>
                  <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortPayments("created_at")}>Ngày {SortIconPayments("created_at")}</th>
                  <th className="text-left py-3 px-4">Hướng</th>
                  <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortPayments("customer_name")}>Đối tượng {SortIconPayments("customer_name")}</th>
                  <th className="text-left py-3 px-4">Phương thức</th>
                  <th className="text-right py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSortPayments("amount")}>Số tiền {SortIconPayments("amount")}</th>
                  <th className="text-left py-3 px-4">Ghi chú</th>
                </tr>
              </thead>
              <tbody data-testid="debts-history-table">
                {sortedPayments.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
                ) : sortedPayments.map((p) => (
                  <tr key={p.payment_id} className="border-t border-border">
                    <td className="py-3 px-4 text-xs text-ink-muted">{formatDateTime(p.created_at)}</td>
                    <td className="py-3 px-4">
                      {p.direction === "in"
                        ? <Badge variant="delivered">Thu</Badge>
                        : <Badge variant="lowstock">Chi</Badge>}
                    </td>
                    <td className="py-3 px-4 font-medium">{p.customer_name || p.supplier_name}</td>
                    <td className="py-3 px-4 text-xs">{t(`payment.${p.method}`)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${p.direction === "in" ? "text-emerald-700" : "text-red-700"}`}>
                      {p.direction === "in" ? "+" : "−"} {formatVND(p.amount)}
                    </td>
                    <td className="py-3 px-4 text-xs text-ink-muted">{p.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* MODAL TRẢ TIỀN NHÀ CUNG CẤP */}
      <Modal open={!!payModal} onClose={() => setPayModal(null)} title={`Trả tiền NCC: ${payModal?.name || ""}`} testId="pay-modal">
        <div className="space-y-3">
          <div>
            <Label>Số tiền trả</Label>
            <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" />
          </div>
          <div>
            <Label>Phương thức</Label>
            <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <option value="cash">Tiền mặt</option>
              <option value="transfer">Chuyển khoản</option>
            </Select>
          </div>
          <div>
            <Label>{t("common.notes")}</Label>
            <Textarea value={payNotes} onChange={(e) => setPayNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPayModal(null)}>{t("common.cancel")}</Button>
            <Button onClick={submitPaySupplier} data-testid="pay-submit">{t("common.confirm")}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}